import { faEllipsis, faExclamationCircle, faLock } from "@fortawesome/free-solid-svg-icons";
import { faCalendarPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { lighten, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import AddEditSchedulePlanning from "./AddEditSchedulePlanning";
import {
  AbsencesContainer,
  AnomalyWrapper,
  EmployeeNameContainer,
  LockIconWrapper,
  MainTableContainer,
  PublicHolidayName,
  ScheduleCode,
  ScheduleHours,
  CellContainer,
  SignedOffContent,
} from "./styles";
import FilterByYearAndMonth from "../../../containers/FilterByYearAndMonth/FilterByYearAndMonth";
import useChangeYearAndMonth from "../../../containers/FilterByYearAndMonth/useChangeYearAndMonth/useChangeYearAndMonth";
import AbsenceButton from "../../../containers/MyCalendar/AddEditAbsence/AbsenceButton";
import EditAbsenceWrapper from "../../../containers/MyCalendar/AddEditAbsence/EditAbsenceWrapper/EditAbsenceWrapper";
import { AbsenceStatusIndicator } from "../../../containers/MyCalendar/AddEditAbsence/styles";
import { CurrentEmployeesQuickfilter } from "../../../containers/Quickfilters/CurrentEmployeesQuickfilter";
import { useCurrentEmployeesQuickFilter } from "../../../containers/Quickfilters/CurrentEmployeesQuickfilter/useCurrentEmployeesQuickFilter";
import QuickfiltersContainer from "../../../containers/Quickfilters/QuickfiltersContainer/QuickfiltersContainer";
import { getIntervalsDuration } from "../../../containers/SystemConfigurationTables/TnAConfig/TnASchedulesTable/TnAScheduleConfig/utils";
import { useAppSelector } from "../../../custom-hooks/redux/redux";
import useAuthorization from "../../../custom-hooks/useAuthorization/useAuthorization";
import useForceUpdate from "../../../custom-hooks/useForceUpdate/useForceUpdate";
import { RefModel } from "../../../models/base/ref-model";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { EmployeeDTO, EmployeeRef } from "../../../models/employees";
import { TnAAbsenceDTO } from "../../../models/tna";
import { TnADayDataRef } from "../../../models/tna/tna-data/tna-day-data-ref";
import { TnACalendarDaysDTO } from "../../../models/tna-calendar-days/tna-calendar-days-dto";
import { TnAOverviewsFilter } from "../../../models/tna-overviews/tna-overview.filter";
import SimplePopover from "../../../reusable-components/SimplePopover/SimplePopover";
import Table from "../../../reusable-components/Table/Table";
import { TnAOverviewsCalendarsApiService } from "../../../services/tna-overviews-calendars/tna-overviews-calendars-api.service";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { FT_MTPAL } from "../../../utils/constants/filter-tables-constants";
import { getDayAndDateAndMonth, intlFormatDateMonth } from "../../../utils/time-utils";
import { openTimesheetViewMode } from "../../my-t-and-a/my-ee-tna-utils";

interface State {
  loading: boolean;
  error: string;
}

const MyTeamPlanning = () => {
  const { t } = useTranslation();
  const tRP = "components.team_planning";
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const today = useMemo(() => new Date(), []);

  const { currentCompanyAccount } = useAppSelector((state) => state.currentUserReducer);

  const { onlyCurrentEmployeesQuickFilter, setOnlyCurrentEmployeesQuickFilter } = useCurrentEmployeesQuickFilter();

  const { year, month, handleChangeYearAndMonth } = useChangeYearAndMonth();

  const theme = useTheme();

  const { forceUpdate } = useForceUpdate();

  const hasAccess = useAuthorization([
    AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves,
    AUTHORIZATIONS.LegalEntityAccess.SimplifiedTeamManagement,
  ]);

  const allowedToViewOnly = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData]);

  const allowedToApproveReject = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const allItems = useRef<TnACalendarDaysDTO[]>([]);
  const tableRef = useRef<HTMLDivElement>(null);

  const [state, setState] = useState<State>({
    loading: false,
    error: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    if (!tableRef.current) return;

    setState((prev) => ({ ...prev, loading: true }));

    const observer = new MutationObserver(() => {
      const dataGridMain = tableRef.current?.querySelector(".MuiDataGrid-main");
      setState((prev) => ({ ...prev, loading: true }));

      if (dataGridMain) {
        const scrollContainer = tableRef.current?.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement;

        if (scrollContainer) {
          if (year === today.getFullYear() && month === today.getMonth()) {
            const currentDay = today.getDate();
            const columnWidth = 120;
            const scrollPosition = currentDay * columnWidth - 360; // center the current day in view
            scrollContainer.scrollTo({
              left: scrollPosition,
              behavior: "instant",
            });
          } else {
            // scroll to start for other months
            scrollContainer.scrollTo({
              left: 0,
              behavior: "instant",
            });
          }
        }

        setState((prev) => ({ ...prev, loading: false }));
        observer.disconnect();
      }
    });

    observer.observe(tableRef.current, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [year, month, today]);

  const clickNavigate = (employee: EmployeeDTO | EmployeeRef) => {
    if (!hasAccess && allowedToViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (hasAccess) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
    }
  };

  const isDateSignedOff = (date: Date, lastSignOffDate: CustomDate | null): boolean => {
    if (!lastSignOffDate) return false;
    const signOffDate = CustomDate.ToDate(lastSignOffDate);
    if (!signOffDate) return false;
    return date <= signOffDate;
  };

  const isDateInTrackingPeriod = (date: Date, startDate: CustomDate | null, endDate: CustomDate | null): boolean => {
    const trackingStart = startDate ? CustomDate.ToDate(startDate) : null;
    const trackingEnd = endDate ? CustomDate.ToDate(endDate) : null;

    if (!trackingStart) return false;

    const isAfterStart = date >= trackingStart;

    if (!trackingEnd) return isAfterStart;

    const isBeforeEnd = date <= trackingEnd;

    return isAfterStart && isBeforeEnd;
  };

  const columns: GridColDef<TnACalendarDaysDTO>[] = [
    {
      field: "employeeCode",
      renderHeader: () => <Typography>{`${t(`common_words.code`)}`}</Typography>,
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <Tooltip title={`${t(`${tRPCommon}.code`)}`}>
          <Typography
            style={{ color: theme.palette.text.disabled }}
            variant={"body2"}
          >
            {row.employee?.employeeCode ?? ""}
          </Typography>
        </Tooltip>
      ),
      align: "center",
      sortable: false,
    },
    {
      field: "name",
      renderCell: ({ row }) => (
        <EmployeeNameContainer
          onClick={() => {
            row.employee && clickNavigate(row.employee);
          }}
          title={row?.employee?.name}
        >
          {row?.employee?.name}
        </EmployeeNameContainer>
      ),
      renderHeader: () => <Typography>{`${t("common_words.name")}`}</Typography>,
      headerAlign: "center",
      flex: 1,
      minWidth: 150,
      align: "center",
      sortable: false,
    },
  ];

  const dynamicColumns = (year: number, month: number) => {
    const result: GridColDef<TnACalendarDaysDTO>[] = [];

    //#region сalculate date range for complete weeks (Monday to Sunday)
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const firstDayOfWeek = firstDayOfMonth.getDay();
    const lastDayOfWeek = lastDayOfMonth.getDay();

    const startDate = new Date(firstDayOfMonth);
    if (firstDayOfWeek === 0) {
      startDate.setDate(startDate.getDate() - 6);
    } else if (firstDayOfWeek >= 2) {
      startDate.setDate(startDate.getDate() - (firstDayOfWeek - 1));
    }

    const endDate = new Date(lastDayOfMonth);
    if (lastDayOfWeek !== 0) {
      endDate.setDate(endDate.getDate() + (7 - lastDayOfWeek));
    }
    //#endregion

    //#region generate columns for each day in the calculated range
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const iterDate = new Date(currentDate);
      // const isCurrentMonth = iterDate.getMonth() === month;

      result.push({
        field: iterDate.toString(),
        renderCell: ({ row }) => {
          const dayOfMonth = iterDate.getDate();
          const monthOfDay = iterDate.getMonth();
          const yearOfDay = iterDate.getFullYear();

          const currDay = row?.daysData?.find((day: TnADayDataRef) => {
            const dayDate = CustomDate.ToDate(day?.tnADate);
            return (
              dayDate &&
              dayDate.getDate() === dayOfMonth &&
              dayDate.getMonth() === monthOfDay &&
              dayDate.getFullYear() === yearOfDay
            );
          });
          const weekday = CustomDate.ToDate(currDay?.tnADate ?? null)?.getDay();

          return currDay?.absences.length ? (
            <AbsencesContainer>
              {currDay?.absences.map((abs: TnAAbsenceDTO, idx: number) => {
                if (idx > 0) return;
                return (
                  <AbsenceStatusIndicator
                    isRejected={abs.isRejected}
                    isCancelled={abs.isCancelled}
                    isApproved={abs.isApproved}
                    key={abs.id}
                  >
                    <EditAbsenceWrapper
                      absence={abs}
                      employee={row.employee}
                      handleNotifyParent={forceUpdate}
                      allowedToApproveReject={allowedToApproveReject}
                      viewMode={allowedToViewOnly && !hasAccess}
                      year={year}
                      month={month}
                      forManager
                      allowedOnlyManagersAction
                    >
                      <AbsenceButton
                        absence={abs}
                        forManager
                      />
                    </EditAbsenceWrapper>
                  </AbsenceStatusIndicator>
                );
              })}

              {currDay?.absences.length > 1 && (
                <SimplePopover
                  buttonType="icon"
                  buttonContent={<FontAwesomeIcon icon={faEllipsis} />}
                  flow="column"
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  {currDay?.absences.slice(1).map((abs: TnAAbsenceDTO, idx: number) => {
                    return (
                      <div
                        style={{
                          marginLeft: "3px",
                          marginRight: "3px",
                          marginBottom: "2px",
                          marginTop: "2px",
                        }}
                        key={idx}
                      >
                        <AbsenceStatusIndicator
                          isRejected={abs.isRejected}
                          isCancelled={abs.isCancelled}
                          isApproved={abs.isApproved}
                          key={abs.id}
                        >
                          <EditAbsenceWrapper
                            absence={abs}
                            employee={row.employee}
                            handleNotifyParent={forceUpdate}
                            allowedToApproveReject={allowedToApproveReject}
                            viewMode={allowedToViewOnly && !hasAccess}
                            year={year}
                            month={month}
                            forManager
                            allowedOnlyManagersAction
                          >
                            <AbsenceButton
                              absence={abs}
                              forManager
                            />
                          </EditAbsenceWrapper>
                        </AbsenceStatusIndicator>
                      </div>
                    );
                  })}
                </SimplePopover>
              )}
            </AbsencesContainer>
          ) : (
            <CellContainer>
              {(currDay?.hasAnomaly ||
                currDay?.anomalyDescription ||
                (currDay?.anomalies && currDay.anomalies.length > 0)) && (
                <AnomalyWrapper>
                  <FontAwesomeIcon
                    className="fa-icon"
                    icon={faExclamationCircle}
                    style={{
                      color: theme.palette.error.main,
                    }}
                    title={`${currDay?.anomalies && currDay.anomalies.length > 1 ? "Anomalies" : "Anomaly"}: ${
                      currDay?.anomalies && currDay.anomalies.length > 0
                        ? currDay.anomalies.map((anomaly) => `${anomaly.anomalyType?.description}`).join(",\n")
                        : currDay?.anomalyDescription
                    }`}
                  />
                </AnomalyWrapper>
              )}

              <React.Fragment>
                <Tooltip
                  title={currDay?.phNames as string}
                  placement="top"
                >
                  <>
                    {!isDateInTrackingPeriod(iterDate, row.timesheetTrackingStartDate, row.timesheetTrackingEndDate) ? (
                      <></>
                    ) : isDateSignedOff(iterDate, row.lastSignedOff) ? (
                      <CellContainer>
                        <LockIconWrapper
                          title={`Changes not allowed: timesheet signed-off until ${intlFormatDateMonth(
                            CustomDate.ToDate(row.lastSignedOff) as Date
                          )}`}
                        >
                          <FontAwesomeIcon icon={faLock} />
                        </LockIconWrapper>
                        <SignedOffContent>
                          <div
                            style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}
                          >
                            {currDay?.schedule ? (
                              <>
                                <ScheduleCode title={`${currDay.schedule?.name}`}>{`${
                                  currDay.schedule?.code?.trim() ? currDay.schedule?.code : "<?>"
                                }`}</ScheduleCode>
                                <ScheduleHours title={`${t(`${tRP}.expected_presence`)}`}>
                                  (
                                  {currDay.schedule
                                    ? `${
                                        getIntervalsDuration(
                                          currDay.schedule.intervals.filter((interval) => interval.weekDay === weekday)
                                        ) /
                                        (1000 * 60 * 60)
                                      } h`
                                    : ""}
                                  )
                                </ScheduleHours>
                              </>
                            ) : null}
                            {currDay?.phNames ? (
                              <PublicHolidayName>{currDay?.phNames as string}</PublicHolidayName>
                            ) : null}
                          </div>
                        </SignedOffContent>
                      </CellContainer>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                        {currDay?.schedule ? (
                          <AddEditSchedulePlanning
                            schedule={currDay.schedule}
                            companyAccount={currDay?.companyAccount ?? currentCompanyAccount ?? null}
                            legalEntity={currDay?.legalEntity ?? row.employee?.legalEntity ?? null}
                            employee={currDay?.employee ?? null}
                            tnADate={currDay?.tnADate ?? null}
                            handleNotifyParent={forceUpdate}
                            // disabled={
                            //   new Date(year, month, i + 1) < new Date(today.getFullYear(), today.getMonth(), today.getDate())
                            // }
                            schedulePlanningId={currDay.schedulePlanningId}
                            row={row}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                cursor: "pointer",
                              }}
                            >
                              <ScheduleCode title={`${currDay.schedule?.name}`}>{`${
                                currDay.schedule?.code?.trim() ? currDay.schedule?.code : "<?>"
                              }`}</ScheduleCode>
                              <ScheduleHours title={`${t(`${tRP}.expected_presence`)}`}>
                                (
                                {currDay.schedule
                                  ? `${
                                      getIntervalsDuration(
                                        currDay.schedule.intervals.filter((interval) => interval.weekDay === weekday)
                                      ) /
                                      (1000 * 60 * 60)
                                    } h`
                                  : ""}
                                )
                              </ScheduleHours>
                            </div>
                          </AddEditSchedulePlanning>
                        ) : (
                          <AddEditSchedulePlanning
                            schedule={null}
                            companyAccount={currDay?.companyAccount ?? currentCompanyAccount ?? null}
                            legalEntity={currDay?.legalEntity ?? row.employee?.legalEntity ?? null}
                            employee={currDay?.employee ?? null}
                            tnADate={currDay?.tnADate ?? null}
                            handleNotifyParent={forceUpdate}
                            // disabled={
                            //   new Date(year, month, i + 1) < new Date(today.getFullYear(), today.getMonth(), today.getDate())
                            // }
                            row={row}
                          >
                            <FontAwesomeIcon
                              icon={faCalendarPlus}
                              style={{
                                cursor: "pointer",
                                width: "24px",
                                height: "24px",
                                color: lighten(theme.palette.primary.light, 0.6),
                                transition: "color 0.2s ease",
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = theme.palette.primary.main)}
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.color = lighten(theme.palette.primary.light, 0.6))
                              }
                              title={t(`components.add_edit_schedule_planning.add_schedule`)}
                            />
                          </AddEditSchedulePlanning>
                        )}
                        {currDay?.phNames && <PublicHolidayName>{currDay?.phNames as string}</PublicHolidayName>}
                      </div>
                    )}
                  </>
                </Tooltip>
              </React.Fragment>
            </CellContainer>
          );
        },
        headerName: getDayAndDateAndMonth(iterDate),
        headerAlign: "center",
        align: "center",
        flex: 1,
        minWidth: 120,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: (params: any) => {
          const currDay = new Date(params.field);
          const dayOfWeek: number = currDay.getDay();
          const dayOfMonth = currDay.getDate();
          const monthOfDay = currDay.getMonth();
          const yearOfDay = currDay.getFullYear();

          const thisDayData = params.row?.daysData?.find((day: TnADayDataRef) => {
            const dayDate = CustomDate.ToDate(day?.tnADate);
            return (
              dayDate &&
              dayDate.getDate() === dayOfMonth &&
              dayDate.getMonth() === monthOfDay &&
              dayDate.getFullYear() === yearOfDay
            );
          });

          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
          const isSignedOff = isDateSignedOff(currDay, params.row?.lastSignOffDate);
          const isOutsideTracking = !isDateInTrackingPeriod(
            currDay,
            params.row?.timesheetTrackingStartDate,
            params.row?.timesheetTrackingEndDate
          );

          return isOutsideTracking
            ? "isOutsideTrackingPeriod"
            : isSignedOff
            ? "isSignedOff"
            : thisDayData?.isPublicHoliday
            ? "isPublicHoliday"
            : isWeekend
            ? "isWeekend"
            : "";
        },
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }
    //#endregion

    return result;
  };

  const defaultItem: Partial<TnACalendarDaysDTO> = {
    employee: null,
    daysData: [],
  };

  const quickfilters = useCallback(
    (items: RefModel[], loading: boolean) => {
      return (
        <QuickfiltersContainer>
          <>
            <CurrentEmployeesQuickfilter
              quickFilters={onlyCurrentEmployeesQuickFilter}
              handleChangeQuickFilters={setOnlyCurrentEmployeesQuickFilter}
              customTRP={tRP}
            />
            <FilterByYearAndMonth
              filterColor={"filter"}
              year={year}
              month={month}
              handleChangeYearAndMonth={handleChangeYearAndMonth}
              withMaxDate={false}
            />
          </>
        </QuickfiltersContainer>
      );
    },
    [onlyCurrentEmployeesQuickFilter, setOnlyCurrentEmployeesQuickFilter, year, month, handleChangeYearAndMonth]
  );

  return (
    <MainTableContainer ref={tableRef}>
      {(hasAccess || allowedToViewOnly) && (
        <Table
          translationRootPath={tRP}
          columns={columns}
          serviceApi={new TnAOverviewsCalendarsApiService()}
          propsFilter={
            {
              onlyCurrentEmployees: onlyCurrentEmployeesQuickFilter.onlyWithCurrentEmployees.onlyCurrentEmployees,
              year,
              month: month + 1,
            } as TnAOverviewsFilter
          }
          defaultItem={defaultItem}
          filter={FT_MTPAL}
          readOnly
          quickFilters={quickfilters}
          dynamicColumns={dynamicColumns(year, month)}
          getAllItems={(items) => {
            allItems.current = items as TnACalendarDaysDTO[];
          }}
          pinnedColumns={{
            left: ["employeeCode", "name"],
          }}
        />
      )}
    </MainTableContainer>
  );
};

export default MyTeamPlanning;
